import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgentService, AgentDto } from '../../../services/agent.service';
import { PDFDocument } from 'pdf-lib';
import { domToCanvas } from 'modern-screenshot';

export interface SubscriptionDetail {
  subscriptionId: string;
  type: string;
  cadence: string;
  amount: number;
  baseAmount: number;
  discountAmount: number;
  status: string;
  startDate: string;
  endDate: string;
  paymentStatus: string;
}

export interface ScrapItemBreakdown {
  scrapItemId: string;
  scrapItemName: string;
  image: string;
  unit: string;
  totalQuantity: number;
  totalDeductedQuantity: number;
  netQuantity: number;
  totalValue: number;
  totalMarketValue: number;
  profitValue: number;
  profitPercentage: number;
  collectionCount: number;
}

export interface AgentBusinessReport {
  agentId: string;
  agentName: string;
  month: number;
  year: number;
  monthName: string;
  totalPickups: number;
  completedPickups: number;
  cancelledAppointments: number;
  rejectedAppointments: number;
  totalScrapVolumeCollected: number;
  totalBusinessValue: number;
  totalMarketValue: number;
  totalGrandTotal: number;
  totalPlatformCharges: number;
  totalCollectionCharges: number;
  profitValue: number;
  profitPercentage: number;
  totalSubscriptionFees: number;
  activeSubscriptionsCount: number;
  subscriptionDetails: SubscriptionDetail[];
  averageRating: number;
  totalRatings: number;
  feedbackCount: number;
  customerFeedbacks: string[];
  scrapItemBreakdown: ScrapItemBreakdown[];
  reportStartDate: string;
  reportEndDate: string;
}

@Component({
  selector: 'app-agent-business-report',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-business-report.component.html',
  styleUrls: ['./agent-business-report.component.css']
})
export class AgentBusinessReportComponent implements OnInit {
  agents: AgentDto[] = [];
  selectedAgentId: string = '';
  selectedMonth: number = new Date().getMonth() + 1;
  selectedYear: number = new Date().getFullYear();
  
  report: AgentBusinessReport | null = null;
  loading: boolean = false;
  error: string = '';
  exportingPdf: boolean = false;

  months = [
    { value: 1, name: 'January' },
    { value: 2, name: 'February' },
    { value: 3, name: 'March' },
    { value: 4, name: 'April' },
    { value: 5, name: 'May' },
    { value: 6, name: 'June' },
    { value: 7, name: 'July' },
    { value: 8, name: 'August' },
    { value: 9, name: 'September' },
    { value: 10, name: 'October' },
    { value: 11, name: 'November' },
    { value: 12, name: 'December' }
  ];

  years: number[] = [];

  constructor(private agentService: AgentService) {
    // Generate last 5 years
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 5; i++) {
      this.years.push(currentYear - i);
    }
  }

  ngOnInit(): void {
    this.loadAgents();
  }

  loadAgents(): void {
    this.agentService.getAgentList(undefined, true, 0, 100).subscribe({
      next: (response) => {
        this.agents = response.items;
      },
      error: (err) => {
        console.error('Error loading agents:', err);
        this.error = 'Failed to load agents';
      }
    });
  }

  loadReport(): void {
    if (!this.selectedAgentId) {
      this.error = 'Please select an agent';
      return;
    }

    this.loading = true;
    this.error = '';
    this.report = null;

    this.agentService.getAgentMonthlyBusinessReport(
      this.selectedAgentId,
      this.selectedMonth,
      this.selectedYear
    ).subscribe({
      next: (data) => {
        this.report = data;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading report:', err);
        this.error = 'Failed to load report. Please try again.';
        this.loading = false;
      }
    });
  }

  async exportToPdf(): Promise<void> {
    if (!this.report) return;

    this.exportingPdf = true;
    this.error = '';

    try {
      const reportElement = document.getElementById('report-content');
      if (!reportElement) {
        throw new Error('Report element not found');
      }

      // Add a class to adjust styles for PDF
      reportElement.classList.add('pdf-export');

      // Small delay to ensure CSS is applied
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture the element as canvas with enhanced options
      const canvas = await domToCanvas(reportElement, {
        scale: 2,
        backgroundColor: '#ffffff',
        filter: (node) => {
          // Filter out elements that might cause issues
          if (node instanceof HTMLElement) {
            return !node.classList.contains('btn') && 
                   !node.classList.contains('filter-actions');
          }
          return true;
        },
        onCloneNode: (clonedNode) => {
          // Additional cleanup in cloned document
          if (clonedNode instanceof HTMLElement && clonedNode.id === 'report-content') {
            // Force computed styles to standard formats
            const allElements = clonedNode.querySelectorAll('*');
            allElements.forEach((el: Element) => {
              const htmlEl = el as HTMLElement;
              const computedStyle = window.getComputedStyle(el);
              
              // Convert any oklch colors to standard format
              if (computedStyle.backgroundColor && computedStyle.backgroundColor.includes('oklch')) {
                htmlEl.style.backgroundColor = 'rgb(255, 255, 255)';
              }
              if (computedStyle.color && computedStyle.color.includes('oklch')) {
                htmlEl.style.color = 'rgb(0, 0, 0)';
              }
            });
          }
        }
      });

      // Remove the PDF class
      reportElement.classList.remove('pdf-export');

      // Create PDF document
      const pdfDoc = await PDFDocument.create();
      
      // Convert canvas to PNG data
      const pngDataUrl = canvas.toDataURL('image/png');
      const pngImageBytes = await fetch(pngDataUrl).then(res => res.arrayBuffer());
      const pngImage = await pdfDoc.embedPng(pngImageBytes);
      
      // A4 size in points: 595.28 x 841.89
      const pageWidth = 595.28;
      const pageHeight = 841.89;
      const margin = 0;
      
      // Calculate scale to fit image width to page width
      const scale = (pageWidth - 2 * margin) / pngImage.width;
      const scaledWidth = pngImage.width * scale;
      const scaledHeight = pngImage.height * scale;
      
      // Calculate how many pages we need
      const pagesNeeded = Math.ceil(scaledHeight / pageHeight);
      
      // For each page, we need to slice the canvas and create a new image
      for (let pageIndex = 0; pageIndex < pagesNeeded; pageIndex++) {
        const page = pdfDoc.addPage([pageWidth, pageHeight]);
        
        // Calculate the slice of the original canvas for this page
        const sourceY = pageIndex * (pageHeight / scale);
        const sourceHeight = Math.min(pageHeight / scale, canvas.height - sourceY);
        
        // Create a temporary canvas for this slice
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sourceHeight;
        const sliceCtx = sliceCanvas.getContext('2d');
        
        if (sliceCtx) {
          // Draw the slice from the original canvas
          sliceCtx.drawImage(
            canvas,
            0, sourceY,                    // source x, y
            canvas.width, sourceHeight,    // source width, height
            0, 0,                          // dest x, y
            canvas.width, sourceHeight     // dest width, height
          );
          
          // Convert slice to PNG and embed in PDF
          const slicePngUrl = sliceCanvas.toDataURL('image/png');
          const slicePngBytes = await fetch(slicePngUrl).then(res => res.arrayBuffer());
          const slicePngImage = await pdfDoc.embedPng(slicePngBytes);
          
          // Calculate dimensions for this slice
          const sliceScaledHeight = sourceHeight * scale;
          
          // Draw the slice on the page (top-aligned)
          page.drawImage(slicePngImage, {
            x: margin,
            y: pageHeight - sliceScaledHeight,
            width: scaledWidth,
            height: sliceScaledHeight,
          });
        }
      }

      // Save PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      const fileName = `Agent_Business_Report_${this.report.agentName.replace(/\s+/g, '_')}_${this.report.monthName}_${this.report.year}.pdf`;
      link.download = fileName;
      link.click();
      
      // Cleanup
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.error = 'Failed to generate PDF. Please try again or contact support.';
      
      // Remove PDF class if still applied
      const reportElement = document.getElementById('report-content');
      if (reportElement) {
        reportElement.classList.remove('pdf-export');
      }
    } finally {
      this.exportingPdf = false;
    }
  }

  getCompletionRate(): number {
    if (!this.report || this.report.totalPickups === 0) return 0;
    return (this.report.completedPickups / this.report.totalPickups) * 100;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}

